import type { LedSpec, Vec2, WallMetrics } from '../types';
import { distanceToOutline } from '../heightfield';
import { clamp01, smoothstep } from '../math/scalar';
import { ledType, type LedType } from './light';
import { createStripRouting, type StripRouting } from './routing';

/**
 * A channel cut into the back of a panel.
 *
 * The other way of backlighting a wall, and the simpler one: instead of a
 * separate box holding the strip at a distance, the strip sits in a groove in
 * the panel's own backing plate. One part per tile rather than two, and it
 * hangs flat against the wall.
 *
 * The groove has two steps, because a strip has two heights. The tape is one
 * thing; the emitters are packages standing on top of it, taller than the tape
 * and narrower. A single groove sized for the tape leaves the LEDs fouling the
 * floor, and one sized for the emitters makes the whole opening that deep for
 * no reason. So the profile is a seat the width of the tape, with a narrower
 * pocket down its centreline for the emitters to stand in.
 *
 * It needs no boolean subtraction, which is the thing worth knowing about it.
 * A panel was never a solid with a groove to cut out of it — it is a displaced
 * top surface, a back, and a skirt joining them. So a channel is just the back
 * surface rising, sampled over the same tessellation by the same machinery,
 * and it comes out watertight for the same reason the top does. A channel that
 * reaches the panel edge makes the skirt shorter there, which is exactly the
 * notch the strip passes through into the next panel.
 *
 * Where the runs go is not decided here. That is `createStripRouting`, the
 * same routing the light box uses, so the two mountings cannot drift apart in
 * where they put the strip — only in what they build around it.
 */

export interface ChannelField {
  active: boolean;
  /** Seat depth for the tape, in millimetres. */
  seatMm: number;
  /** Extra depth of the emitter pocket below the seat, in millimetres. */
  emitterMm: number;
  /** Width of the emitter pocket, in millimetres. */
  emitterWidthMm: number;
  /** Deepest point of the groove: seat plus emitter pocket, in millimetres. */
  depth: number;
  /** Material left over the deepest point, which the light crosses, in mm. */
  diffuserMm: number;
  /** Height of the back surface under a point: `0` is the back face. */
  backHeight(worldX: number, worldY: number, localX: number, localY: number): number;
  routing: StripRouting;
}

const NO_ROUTING: StripRouting = {
  active: false,
  coverage: () => 0,
  crossSection: 0,
  runSpacingMm: 0,
};

const INACTIVE: ChannelField = {
  active: false,
  seatMm: 0,
  emitterMm: 0,
  emitterWidthMm: 0,
  depth: 0,
  diffuserMm: 0,
  backHeight: () => 0,
  routing: NO_ROUTING,
};

/** The least material that may be left over a channel, in millimetres. */
export const MIN_DIFFUSER_MM = 0.8;

export interface ChannelProfile {
  seatMm: number;
  emitterMm: number;
  /** Backing plate that would take the whole strip at full depth, in mm. */
  plateForRequestMm: number;
}

/**
 * How deep each step can actually be cut, given the panel's own thickness.
 *
 * The groove lives in the backing plate and never in the relief above it. That
 * keeps its floor flat, which a strip needs to seat against, and leaves
 * material over it everywhere rather than only where the relief happens to be
 * deep.
 *
 * When the plate cannot take both steps, the emitter pocket wins. A seat that
 * is too shallow leaves the tape standing a little proud of the panel's back,
 * which is untidy; a pocket that is too shallow means the LEDs do not fit at
 * all, which is not a wall.
 */
export function channelProfile(
  spec: LedSpec,
  type: LedType,
  baseThicknessMm: number,
): ChannelProfile {
  const available = Math.max(0, baseThicknessMm - MIN_DIFFUSER_MM);
  const emitterMm = Math.min(type.emitterMm, available);
  return {
    emitterMm,
    seatMm: Math.max(0, Math.min(spec.channelDepth, available - emitterMm)),
    plateForRequestMm: spec.channelDepth + type.emitterMm + MIN_DIFFUSER_MM,
  };
}

export interface ChannelOptions {
  spec: LedSpec;
  wall: WallMetrics;
  baseThicknessMm: number;
  /** Panel footprint, in panel space. */
  outline: readonly Vec2[];
  /** Distance between mesh samples, in millimetres. */
  sampleSpacingMm: number;
}

export function createChannelField(options: ChannelOptions): ChannelField {
  const { spec, wall, baseThicknessMm, outline, sampleSpacingMm } = options;
  const type = ledType(spec.ledType);
  const { seatMm, emitterMm } = channelProfile(spec, type, baseThicknessMm);
  const depth = seatMm + emitterMm;

  if (!spec.enabled || spec.stripWidth <= 0 || depth <= 0.05) return INACTIVE;

  // Wide enough that a groove wall lands on a sample or two and prints as a
  // slope rather than a staircase, and no wider: every millimetre of ramp
  // widens the opening in the finished part.
  const ramp = Math.max(0.6, sampleSpacingMm * 0.75);
  const routing = createStripRouting({ spec, wall, interior: outline, ramp });

  // The pocket is the same routing at a narrower width, so the two steps sit
  // on exactly the same centreline — they cannot drift apart, because they are
  // the same runs read at two widths.
  const emitterWidthMm = Math.min(type.emitterWidthMm, spec.stripWidth);
  const pocket =
    emitterMm > 0.01
      ? createStripRouting({
          spec: { ...spec, stripWidth: emitterWidthMm },
          wall,
          interior: outline,
          ramp,
        })
      : NO_ROUTING;

  // Turning pass-through off has to actually close the groove, or the toggle
  // is decoration: in a light box it stops a slot being cut, but a channel has
  // no rim to leave standing. So the run is faded out before it reaches the
  // edge, which leaves the skirt full height there and the strip contained.
  const margin = spec.stripWidth / 2 + ramp;
  const edgeMask = (localX: number, localY: number): number =>
    spec.passThrough
      ? 1
      : clamp01(smoothstep(0, margin, distanceToOutline(localX, localY, outline)));

  return {
    active: routing.active,
    seatMm,
    emitterMm,
    emitterWidthMm,
    depth,
    diffuserMm: baseThicknessMm - depth,
    routing,
    backHeight: (worldX, worldY, localX, localY) => {
      // The deeper, narrower step wins wherever it reaches, which is what
      // makes the profile a step rather than a single flat groove.
      const seat = seatMm * routing.coverage(worldX, worldY, localX, localY);
      const full = depth * pocket.coverage(worldX, worldY, localX, localY);
      return Math.max(seat, full) * edgeMask(localX, localY);
    },
  };
}
